/** @forge-header
 * @id          parse
 * @role        Ticker parser orchestrator | prefix-based dispatch to sport parsers
 * @pattern     orchestrator
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/sports/*:parsers | kalshiTicker/parsers/*:parsers | kalshiTicker/helpers:parseFallback
 * @downstream  kalshiTicker/index (barrel) | components/atoms/TickerDisplay
 * @exports     parseKalshiTicker:fn | nflTeamFromCode:fn | nhlTeamFromCode:fn
 * @test        parse.test.ts (26 cases)
 */

import type { ParsedTicker } from './types';
import { parseFallback, nflTeamFromCode, nhlTeamFromCode } from './helpers';
import type { TickerParser } from './helpers';

// Sport parsers
import { parseNbaGame, parseNbaSeason } from './sports/nba';
import { parseWnbaGame, parseWnbaSeason } from './sports/wnba';
import { parseNflGame, parseNflExtended, parseSuperBowl, NFL_PROP_SERIES_LONGEST_FIRST } from './sports/nfl';
import { parseNhlGame } from './sports/nhl';
import { parseMlbGame, parseMlbExtended } from './sports/mlb';
import { parseNcaaMbGame, parseNcaaWbGame, parseNcaaGame } from './sports/ncaa';
import { parseCfbGame, parseCfbSpread, parseCfbTotal } from './sports/cfb';
import { parseAtpMatch, parseAtpChallengerMatch, parseWtaMatch, parseItfMatch, parseItfwMatch } from './sports/tennis';
import { parseUfcFight } from './sports/ufc';
import { parseBoxing } from './sports/boxing';
import { parseEsportsGame } from './sports/esports';
import { parseCricketMatch } from './sports/cricket';
import { parseSoccerGame, parseSoccerBtts, parseSoccerAdvance } from './sports/soccer';

// Market type parsers
import {
  parseNbaSpread,
  parseWnbaSpread,
  parseNflSpread,
  parseNhlSpread,
  parseNcaaMbSpread,
  parseNcaaWbSpread,
  parseMlbSpread,
  parseSoccerSpread,
} from './parsers/spreads';
import {
  parseNbaTotal,
  parseWnbaTotal,
  parseNflTotal,
  parseNhlTotal,
  parseNcaaMbTotal,
  parseNcaaWbTotal,
  parseMlbTotal,
  parseSoccerTotal,
} from './parsers/totals';
import { parseSeriesWinner } from './parsers/series';

/**
 * Parsers by prefix — longer prefixes listed first to match correctly
 * (e.g., KXNBAGAME before KXNBA)
 */
const PARSERS: Array<[string, TickerParser]> = [
  // WNBA — listed before NBA. KXW != KXN so prefixes don't collide, but
  // keeping WNBA first matches hydra's matcher order and avoids surprises
  // if anything is ever shortened to a common prefix.
  ['KXWNBASERIES', parseSeriesWinner],
  ['KXWNBASPREAD', parseWnbaSpread],
  ['KXWNBATOTAL', parseWnbaTotal],
  ['KXWNBAGAME', parseWnbaGame],
  ['KXWNBA', parseWnbaSeason],
  // NBA — series prefix listed before KXNBA so playoff tickers don't fall
  // through to the season-prop parser.
  ['KXNBASERIES', parseSeriesWinner],
  ['KXNBASPREAD', parseNbaSpread],
  ['KXNBATOTAL', parseNbaTotal],
  ['KXNBAGAME', parseNbaGame],
  ['KXNBA', parseNbaSeason],
  // NFL
  ['KXNFLSPREAD', parseNflSpread],
  ['KXNFLTOTAL', parseNflTotal],
  // NFL per-game player props (nfl-player-props) — one parser, prefix-
  // dispatched, registered LONGEST-FIRST from the series table so KXNFLRECYDS
  // and KXNFLRRYDS land before KXNFLREC and KXNFLPASSINT before KXNFLINT (the
  // KXMLBHRR/KXMLBHR hazard below). None starts with KXNFL1H/2H, so the half
  // regex above never sees them.
  ...NFL_PROP_SERIES_LONGEST_FIRST.map((s): [string, TickerParser] => [s, parseNflExtended]),
  ['KXNFLGAME', parseNflGame],
  ['KXSB', parseSuperBowl],
  // NHL
  ['KXNHLSERIES', parseSeriesWinner],
  ['KXNHLSPREAD', parseNhlSpread],
  ['KXNHLTOTAL', parseNhlTotal],
  ['KXNHLGAME', parseNhlGame],
  // MLB — TEAMTOTAL before TOTAL before TB (longest-first convention; the one
  // REAL startsWith collision in this block is HRR/HR — KXMLBHRR must be
  // listed before KXMLBHR or every H+R+RBI ticker parses as a home-run prop).
  ['KXMLBSERIES', parseSeriesWinner],
  ['KXMLBSPREAD', parseMlbSpread],
  ['KXMLBTEAMTOTAL', parseMlbExtended],
  ['KXMLBTOTAL', parseMlbTotal],
  ['KXMLBGAME', parseMlbGame],
  // MLB player props (yes-ladders) + per-game event markets — all share the
  // KXMLBGAME event core, all Kalshi-only. One parser, prefix-dispatched.
  ['KXMLBHIT', parseMlbExtended],
  ['KXMLBHRR', parseMlbExtended],
  ['KXMLBHR', parseMlbExtended],
  ['KXMLBRBI', parseMlbExtended],
  ['KXMLBTB', parseMlbExtended],
  ['KXMLBSB', parseMlbExtended],
  ['KXMLBKS', parseMlbExtended],
  ['KXMLBOUTS', parseMlbExtended],
  ['KXMLBRFI', parseMlbExtended],
  ['KXMLBEXTRAS', parseMlbExtended],
  // NCAA — longer prefixes first
  ['KXNCAAMBSPREAD', parseNcaaMbSpread],
  ['KXNCAAMBTOTAL', parseNcaaMbTotal],
  ['KXNCAAMBGAME', parseNcaaMbGame],
  ['KXNCAAWBSPREAD', parseNcaaWbSpread],
  ['KXNCAAWBTOTAL', parseNcaaWbTotal],
  ['KXNCAAWBGAME', parseNcaaWbGame],
  ['KXNCAAGAME', parseNcaaGame],
  // College Football
  ['KXNCAAFSPREAD', parseCfbSpread],
  ['KXNCAAFTOTAL', parseCfbTotal],
  ['KXNCAAFGAME', parseCfbGame],
  // Tennis (ITF folds into ATP/WTA tabs via marketStore). Challenger is its
  // own Kalshi series (#604) — not a prefix of KXATPMATCH nor prefixed by it,
  // so ordering in this block cannot collide.
  ['KXATPCHALLENGERMATCH', parseAtpChallengerMatch],
  ['KXATPMATCH', parseAtpMatch],
  ['KXWTAMATCH', parseWtaMatch],
  ['KXITFWMATCH', parseItfwMatch],
  ['KXITFMATCH', parseItfMatch],
  // UFC / MMA
  ['KXUFCFIGHT', parseUfcFight],
  // Boxing
  ['KXBOXING', parseBoxing],
  // Esports — one parser for all titles (CS2 / Dota2 / Valorant / LoL)
  ['KXCS2GAME', parseEsportsGame],
  ['KXDOTA2GAME', parseEsportsGame],
  ['KXVALORANTGAME', parseEsportsGame],
  ['KXLOLGAME', parseEsportsGame],
  // Cricket
  ['KXWT20MATCH', parseCricketMatch],
  // Soccer — 3-way moneyline (Home/Away/Draw) grouped via marketStore
  // multi-outcome path; spread/total are 2-way binaries per line (full-game
  // only). SPREAD/TOTAL listed before GAME so the longer-suffix prefixes win
  // (no actual collision — GAME != SPREAD/TOTAL — but matches the file's
  // longest-first convention).
  ['KXINTLFRIENDLYSPREAD', parseSoccerSpread],
  ['KXINTLFRIENDLYTOTAL', parseSoccerTotal],
  ['KXINTLFRIENDLYGAME', parseSoccerGame],
  ['KXUECLADVANCE', parseSoccerAdvance],
  ['KXUECLSPREAD', parseSoccerSpread],
  ['KXUECLTOTAL', parseSoccerTotal],
  ['KXUECLGAME', parseSoccerGame],
  // To Advance — knockout 2-way. Listed before GAME so the longer ADVANCE suffix
  // wins (no real collision — ADVANCE != GAME — but matches the longest-first
  // convention). Knockout-only (World Cup R32 onward; UECL parity).
  // Club leagues — MLS, UEFA Europa League (UEL), UEFA Champions League (UCL).
  // Distinct competitions from the Conference League (KXUECL*) above; `KXUECL`
  // does not prefix-match `KXUEL`/`KXUCL`, so without these every club-league
  // position rendered as a truncated raw ticker.
  ['KXMLSSPREAD', parseSoccerSpread],
  ['KXMLSTOTAL', parseSoccerTotal],
  ['KXMLSGAME', parseSoccerGame],
  ['KXUELSPREAD', parseSoccerSpread],
  ['KXUELTOTAL', parseSoccerTotal],
  ['KXUELGAME', parseSoccerGame],
  ['KXUCLSPREAD', parseSoccerSpread],
  ['KXUCLTOTAL', parseSoccerTotal],
  ['KXUCLGAME', parseSoccerGame],
  ['KXEPLSPREAD', parseSoccerSpread],
  ['KXEPLTOTAL', parseSoccerTotal],
  ['KXEPLGAME', parseSoccerGame],
  ['KXSERIEASPREAD', parseSoccerSpread],
  ['KXSERIEATOTAL', parseSoccerTotal],
  ['KXSERIEAGAME', parseSoccerGame],
  ['KXLALIGASPREAD', parseSoccerSpread],
  ['KXLALIGATOTAL', parseSoccerTotal],
  ['KXLALIGAGAME', parseSoccerGame],
  ['KXBUNDESLIGASPREAD', parseSoccerSpread],
  ['KXBUNDESLIGATOTAL', parseSoccerTotal],
  ['KXBUNDESLIGAGAME', parseSoccerGame],
  ['KXWCADVANCE', parseSoccerAdvance],
  ['KXWCSPREAD', parseSoccerSpread],
  ['KXWCTOTAL', parseSoccerTotal],
  ['KXWCGAME', parseSoccerGame],
];

/** NBA + WNBA period markets share the full-game ticker shape after the series
 *  prefix — `KXNBA1QTOTAL-26JUN05NYKSAS-3` vs `KXNBATOTAL-26JUN05NYKSAS-3`,
 *  `KXWNBA1HTOTAL-26JUN05CONNCHI-84` vs `KXWNBATOTAL-…`. Without this, the
 *  period prefix falls through the dispatch to the `KXNBA`/`KXWNBA` season-prop
 *  catch-all and parses to garbage (no game ⇒ raw-ticker fallback on the
 *  Positions page, no canonical rollup). Capture group 1 = league prefix. */
const PERIOD_RE = /^(KXW?NBA)(1H|2H|[1-4]Q)(SPREAD|TOTAL|WINNER)(-.*)$/;

/** Parse any Kalshi ticker into structured data */
export function parseKalshiTicker(ticker: string): ParsedTicker {
  const raw = ticker.trim().toUpperCase();

  // Normalize an NBA/WNBA period ticker to its full-game series (same league),
  // parse with the existing parsers, then re-tag the original series + period.
  // The period moneyline (WINNER, 3-way incl. TIE) maps to the GAME parser.
  // Result: same game / date / outcome as the full-game shape (so it rolls up
  // under the canonical game), just annotated with `period` for the side label.
  const periodMatch = raw.match(PERIOD_RE);
  if (periodMatch) {
    const [, league, period, betType, rest] = periodMatch;
    const fullSeries = betType === 'WINNER' ? `${league}GAME` : `${league}${betType}`;
    const parsed = parseKalshiTicker(`${fullSeries}${rest}`);
    return {
      ...parsed,
      raw,
      series: `${league}${period}${betType}`,
      period: period as ParsedTicker['period'],
    };
  }

  // MLB First 5 Innings (F5): KXMLBF5 (3-way moneyline — BARE prefix, no GAME
  // suffix), KXMLBF5SPREAD, KXMLBF5TOTAL. Normalize to the full-game MLB series,
  // parse with the existing MLB parsers, then re-tag the F5 series + period. F5
  // resolves to the SAME game / date / outcome as the full game (different
  // period) so it rolls up under the canonical game; Kalshi-only (Polymarket has
  // no inning markets). The bare moneyline maps to KXMLBGAME, not KXMLBSPREAD.
  const mlbF5Match = raw.match(/^KXMLBF5(SPREAD|TOTAL)?(-.*)$/);
  if (mlbF5Match) {
    const [, betType, rest] = mlbF5Match;
    const fullSeries = betType ? `KXMLB${betType}` : 'KXMLBGAME';
    const parsed = parseKalshiTicker(`${fullSeries}${rest}`);
    return {
      ...parsed,
      raw,
      series: `KXMLBF5${betType ?? ''}`,
      period: 'F5',
    };
  }

  // NFL halves: bare KXNFL1H / KXNFL2H (the half moneyline — no WINNER suffix,
  // as MLB's bare KXMLBF5 is for innings) plus KXNFL{1H,2H}{SPREAD,TOTAL}.
  // Normalize to the full-game NFL series, parse with the existing NFL parsers,
  // then re-tag the half series + period. Same game / date / outcome as the
  // full-game shape, so a half rolls up under the canonical game rather than
  // forming a second ladder.
  //
  // Delegating (rather than adding six parsers) keeps ONE authority on the NFL
  // away/home split. parseNflGame/parseNflSpread/parseNflTotal anchor on the
  // team-code registry, which is what makes variable-width codes resolve (GB,
  // KC, NE alongside DET, CIN); the fixed slice they replaced mis-rendered 52
  // of 64 live tickers (#356/#357). Six copies would be six places to regress
  // that, and a second authority on team identity inside a label parser.
  //
  // KXNFL1HWINNER / KXNFL2HWINNER are deliberately NOT matched: the #511 audit
  // found no market evidence for either, so they stay fail-closed instead of
  // being aliased onto the bare-moneyline shape. The `-` anchor in group 3 is
  // what enforces that — `KXNFL1HWINNER-…` has no `-` where it needs one.
  // Quarters (KXNFL1Q–4Q, nfl-game-lines-props 2026-09-03) take the same
  // delegation — they share the halves' shape exactly. The quarter BTTS
  // (KXNFL1QBTTS) is NOT matched: it is a wire-classified yes/no with no
  // full-game equivalent, and describeMarket reads it off the frame.
  const nflHalfMatch = raw.match(/^KXNFL(1H|2H|[1-4]Q)(SPREAD|TOTAL)?(-.*)$/);
  if (nflHalfMatch) {
    const [, period, betType, rest] = nflHalfMatch;
    const fullSeries = betType ? `KXNFL${betType}` : 'KXNFLGAME';
    const parsed = parseKalshiTicker(`${fullSeries}${rest}`);
    return {
      ...parsed,
      raw,
      series: `KXNFL${period}${betType ?? ''}`,
      period: period as ParsedTicker['period'],
    };
  }

  // NHL regulation periods (#1218): bare KXNHL{1,2,3}P (the 3-way period
  // winner, TEAM|TIE) plus KXNHL{1,2,3}P{SPREAD,TOTAL}. Same delegation as the
  // NFL halves above, so the NHL away/home split stays with the registry
  // parsers (splitNhlTeams — 2-letter LA/SJ/TB/NJ). The period BTTS
  // (KXNHL1PBTTS) has no full-game equivalent and is not matched.
  const nhlPeriodMatch = raw.match(/^KXNHL([1-3]P)(SPREAD|TOTAL)?(-.*)$/);
  if (nhlPeriodMatch) {
    const [, period, betType, rest] = nhlPeriodMatch;
    const fullSeries = betType ? `KXNHL${betType}` : 'KXNHLGAME';
    const parsed = parseKalshiTicker(`${fullSeries}${rest}`);
    return {
      ...parsed,
      raw,
      series: `KXNHL${period}${betType ?? ''}`,
      period: period as ParsedTicker['period'],
    };
  }

  // Soccer Game Lines: period (1H/2H) spread/total/moneyline + BTTS (any scope).
  // Period spread/total/moneyline normalize to the full-game soccer parser and
  // re-tag the period (bare KXWC1H/KXWC2H ⇒ the GAME parser); BTTS parses
  // directly (no full-game equivalent). Full-game spread/total/moneyline (no
  // period, not BTTS) are NOT matched here — they fall through to PARSERS below.
  const socMatch = raw.match(/^(KX(?:WC|UECL|INTLFRIENDLY))(1H|2H)?(SPREAD|TOTAL|BTTS)?-(.*)$/);
  if (socMatch && (socMatch[2] || socMatch[3] === 'BTTS')) {
    const [, league, period, betType, rest] = socMatch;
    const per = period as ParsedTicker['period'] | undefined;
    if (betType === 'BTTS') {
      return parseSoccerBtts(raw, rest, per);
    }
    const fullSeries = betType ? `${league}${betType}` : `${league}GAME`;
    // The bare period moneyline goes STRAIGHT to the soccer game parser with
    // the ORIGINAL ticker, rather than through a rebuilt `KXWCGAME-…` string.
    // That parser reads only the date / pack / outcome segments and this branch
    // overrides `series` anyway, so the rebuild bought nothing — but since #837
    // an unresolved leg carries the exact ticker in its own label, and the
    // rebuilt string would bake the FULL-GAME sibling's ticker into the 1H/2H
    // leg's label ("Soccer: KXWCGAME-…-TIE · Draw" on a KXWC1H market).
    // Spread/total keep the rebuild: they have their own parsers, and their
    // labels are spelled with team codes rather than the raw ticker.
    const parsed = betType
      ? parseKalshiTicker(`${fullSeries}-${rest}`)
      : parseSoccerGame(raw);
    return {
      ...parsed,
      raw,
      series: `${league}${period ?? ''}${betType ?? ''}`,
      period: per,
    };
  }

  for (const [prefix, parser] of PARSERS) {
    if (raw.startsWith(prefix)) {
      return parser(raw);
    }
  }

  return parseFallback(raw);
}

import { detectVenue, parsePolymarketTicker } from './polymarket';

/**
 * Venue-agnostic ticker parser. Detects venue from the prefix and
 * dispatches to the appropriate sub-parser. Always returns a
 * `ParsedTicker` (never null) so callers can use a single render path.
 */
export function parseTicker(ticker: string): ParsedTicker {
  return detectVenue(ticker) === 'polymarket'
    ? parsePolymarketTicker(ticker)
    : parseKalshiTicker(ticker);
}

export { nflTeamFromCode, nhlTeamFromCode };
export { detectVenue, parsePolymarketTicker } from './polymarket';
